import { CourseType } from '../../../constants/course-type';
import { Fee, FeesNotes } from '../../../models/course/ICourseContent';
import { ICoursePricing } from '../../../models/course/ICourseData';

export type FeeRow = {
  title: string;
  value: string;
};

export type FeesAndPricingData = {
  feesList: FeeRow[];
  feesTotal: FeeRow;
  feesAndPricingNotes: FeesNotes[];
  feesNote: string;
};

function getFeesNoteData(feesAndPricingNotes: FeesNotes[], route: string) {
  let feesNote: string;

  const allRouteNote = feesAndPricingNotes?.find((note) => note?.fields?.programRoutes === 'All');

  const courseTypeKey = Object.keys(CourseType).find((x) => x === route);
  const courseType = CourseType[courseTypeKey];

  const routeNotes = feesAndPricingNotes?.filter(
    (note) => note?.fields?.programRoutes === courseType,
  );

  // get fees note based upon the Program Route
  if (routeNotes?.length > 0 || allRouteNote) {
    const noteSource = routeNotes[0] || allRouteNote;
    feesNote = noteSource?.fields?.feesNote;
  }

  return {
    feesAndPricingNotes,
    feesNote,
  };
}

export function getFeesAndPricingData(
  tuitionFee: ICoursePricing,
  fees: Fee[],
  feesAndPricingNotes: FeesNotes[],
  countryCode: string,
  route: string,
): FeesAndPricingData {
  const feesList: FeeRow[] = [];

  let minTotal = 0;
  let maxTotal = 0;

  if (tuitionFee) {
    feesList.push({
      title: 'Tuition fee',
      value: getPrice(countryCode, tuitionFee.amount),
    });
    minTotal += tuitionFee.amount;
    maxTotal += tuitionFee.amount;
  }

  if (fees?.length) {
    fees.forEach((x) => {
      if (!x.fields) {
        return;
      }

      feesList.push({
        title: x.fields.title,
        value: getPrice(countryCode, x.fields.minFee, x.fields.maxFee),
      });
      minTotal += x.fields.minFee;
      maxTotal += x.fields.maxFee ? x.fields.maxFee : x.fields.minFee;
    });
  }

  const feesTotal: FeeRow = {
    title: 'Approximate cost',
    value: getPrice(countryCode, minTotal, maxTotal),
  };

  return {
    feesList,
    feesTotal,
    ...getFeesNoteData(feesAndPricingNotes, route),
  };
}

export function getPrice(countryCode: string, minFee: number, maxFee?: number): string {
  let result = `${getCurrencySymbol(countryCode)} ${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minFee)}`;

  if (maxFee && minFee < maxFee) {
    result += ` - ${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(maxFee)}`;
  }

  return result;
}

export function getCurrencySymbol(countryCode: string): string {
  switch (countryCode?.toLocaleLowerCase()) {
    case 'gb':
      return '£';
    case 'us':
      return '$';
    case 'au':
      return 'A$';
    case 'sa':
      return 'SAR';
    case 'ae':
      return 'AED';
    default:
      return '$';
  }
}
